import classNames from "classnames";
import { Fragment } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/shared/DropdownMenu";
import Icon from "~/components/shared/Icon";
import CategoryOptions from "~/components/build/Show/components/Filter/CategoryOptions";
import { FilterCategory, SelectedFilter } from "~/components/build/Show/components/Filter/types";

type Props = {
  categories: FilterCategory[];
  selected: SelectedFilter[];
  onChange: (next: SelectedFilter[]) => void;
  /** Test id for the trigger button. Defaults to "filter-menu". */
  testId?: string;
  /** Label shown in the trigger. Defaults to "Filter". */
  triggerLabel?: string;
};

// Cap the dropdown body height to the available space so long option lists
// (e.g. branches) stay scrollable inside the viewport.
const scrollableContentClassName =
  "max-h-[var(--radix-dropdown-menu-content-available-height,50vh)] overflow-y-auto px-0 py-1";

const groupLabelClassName =
  "text-charcoal-400 px-3 py-1 text-xs font-medium normal-case tracking-normal";

/**
 * Dropdown filter menu. Renders each `FilterCategory` as an inline group: a
 * heading row (the category label) followed by the category's checkbox
 * options. Groups are visually separated. Selecting an option toggles it via
 * `onChange` without closing the menu.
 */
export default function FilterMenu({
  categories,
  selected,
  onChange,
  testId = "filter-menu",
  triggerLabel = "Filter",
}: Props) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild={true}>
        <button
          type="button"
          data-testid={testId}
          className={classNames(
            "inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm transition-colors hover:bg-purple-100 hover:text-purple-600 focus:z-10",
            selected.length > 0 ? "bg-purple-100 text-purple-600" : "text-charcoal-700",
          )}
        >
          <Icon icon="custom/outline/16x/filter" className="h-4 w-4 shrink-0" />
          <span>{triggerLabel}</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuPortal>
        <DropdownMenuContent
          className={scrollableContentClassName}
          align="start"
          alignOffset={-1}
          data-testid={`${testId}-content`}
        >
          {categories.map((category, index) => (
            <Fragment key={category.key}>
              {index > 0 && <DropdownMenuSeparator className="mx-0 my-1 bg-gray-300" />}
              <DropdownMenuLabel
                className={groupLabelClassName}
                data-testid={`${testId}-${category.key}-label`}
              >
                {category.label}
              </DropdownMenuLabel>
              <CategoryOptions
                category={category}
                selected={selected}
                onChange={onChange}
                testId={testId}
              />
            </Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
